import React, { useEffect, useRef, useState } from 'react';
import {
  UnitSystem,
  CustomUnitFields,
  ProjectDto,
  ModelNodeDataForm,
  removeAddedNodeMutation,
  useAddNodeMutation,
  useAddSpoolsMutation
} from 'providers/api';
import { enqueueSnackbar } from 'notistack';
import { Trans } from '@lingui/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Add } from '@mui/icons-material';
import {
  array,
  number,
  string,
  object,
  SchemaOf
} from 'yup';
import { i18n } from 'i18n';
import { Weaken } from 'utils';
import useForm from 'hooks/useForm';
import { useDebounce } from 'use-debounce';
import { FormikActions } from 'components/Form';
import { invalidate } from '@react-three/fiber';
import StringNodesTable from './StringNodesTable';
import { NodeRow } from '../../types';
import {
  NodeValidationResult,
  validateNodeRowCoordinates,
  validateNodesUniqueness
} from '../../utils/validation';
import {
  createNodeAndSpool,
  createNodeFromRow,
  createSpools,
  edgesForNodeRow,
  PREVIEW_SPOOL_COLOUR,
  toSpoolImportDto
} from '../../utils/nodeCreation';
import { PCNode } from '../../../MineModel/Nodes';
import { Model } from '../../../hooks/useModelState';
import { checkDistanceToCenter } from '../../hooks/sharedImportUtils';

interface StringNodesFormProps {
  project: ProjectDto;
  model: Model;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  closeDialog: () => void;
  isAdminOrPc?: boolean;
}

const BetweenNodesSchema: SchemaOf<NodeRow> = object().shape({
  nodeId: string().required(i18n._('Node ID is required')),
  previousNodeId: array().of(string().required()).min(1, i18n._('Previous Node is required')),
  pipeType: string().required(i18n._('Pipe Type is required')),
  x: number().required(i18n._('X coordinate is required')),
  y: number().required(i18n._('Y coordinate is required')),
  z: number().required(i18n._('Z coordinate is required')),
  endAtExistingNode: string(),
  frictionLoopLength: number(),
  area: string(),
  level: string(),
  pitName: string(),
});

// A string of nodes is N nodes plus N-1 spools: each row joins the row before it, and the last row may
// also terminate on an existing node. The flow-loop length a row carries belongs to its incoming edge.
const buildChainRows = (rows: Weaken<NodeRow>[]): NodeRow[] => rows.map((row, index) => ({
  nodeId: row.nodeId || '',
  x: Number(row.x) || 0,
  y: Number(row.y) || 0,
  z: Number(row.z) || 0,
  pipeType: row.pipeType || '',
  previousNodeId: (index > 0 ? [rows[index - 1].nodeId ?? ''] : (row.previousNodeId ?? [])) as string[],
  pitName: row.pitName,
  frictionLoopLength: row.frictionLoopLength,
  area: row.area,
  level: row.level,
  endAtExistingNode: index === rows.length - 1 ? row.endAtExistingNode || '' : '',
}));

export const StringNodesForm: React.FC<StringNodesFormProps> = ({
  project,
  model,
  unitSystemPreference,
  customUnitFields,
  closeDialog,
  isAdminOrPc,
}) => {
  const addStringNodesMutation = useAddNodeMutation();
  const addSpoolsMutation = useAddSpoolsMutation(project.entityId);
  const removeAddedNodes = removeAddedNodeMutation();
  const [validationError, setValidationError] = useState<string | null>(null);

  const initialNodeRow: Weaken<NodeRow> = {
    nodeId: '',
    pitName: '',
    previousNodeId: [],
    pipeType: '',
    area: '',
    level: '',
  };

  // ---------- Validation helpers ----------
  const validateNodes = (nodes: Weaken<NodeRow>[]): NodeValidationResult[] => {
    const validationErrors: NodeValidationResult[] = [];
    const existingNodes = Object.values(model.nodes);

    const uniquenessResult = validateNodesUniqueness(nodes);
    if (!uniquenessResult.isValid) validationErrors.push(uniquenessResult);

    if (!isAdminOrPc) {
      const distanceResult = checkDistanceToCenter(model, nodes);
      if (!distanceResult.isValid) validationErrors.push(distanceResult);
    }

    nodes.forEach((node) => {
      const vr = validateNodeRowCoordinates(node as NodeRow, existingNodes);
      if (!vr.isValid) validationErrors.push(vr);
    });

    return validationErrors;
  };

  // ---------- Form ----------
  const { formik, helpers } = useForm<{ nodes: Weaken<NodeRow>[] }>({
    mutation: addStringNodesMutation,
    formikConfig: {
      initialValues: { nodes: [initialNodeRow] },
      onSubmit: async (values, { setSubmitting }) => {
        setSubmitting(true);
        setValidationError(null);

        const chainRows = buildChainRows(values.nodes);

        const data: ModelNodeDataForm[] = chainRows.map((row) => ({
          nodeId: row.nodeId,
          point: { x: row.x, y: row.y, z: row.z },
          pipeId: row.pipeType,
          pit: row.pitName,
          isStope: false,
          isPit: !!row.pitName,
          stopeData: undefined,
          level: row.level,
          area: row.area,
          isPlantNode: false,
          ruptureDiskPressure: 0,
          chokeHead: 0,
        }));

        // The new nodes do not exist server-side yet, so the spools are minted against an index that
        // already contains them - geometry is all getDefaultFlowFlags needs.
        const nodeIndex = new Map<string, PCNode>(Object.values(model.nodes).map((node) => [node.id, node]));
        chainRows.forEach((row) => nodeIndex.set(row.nodeId, createNodeFromRow(row, false)));
        const spools = createSpools(chainRows.flatMap(edgesForNodeRow), model, { nodeIndex });

        // Sequential, never parallel: both writes hit the one-per-mine reticulation document.
        try {
          await addStringNodesMutation.mutateAsync({
            projectId: project.entityId,
            projectName: project.name,
            nodeId: '',
            data,
          });

          if (spools.length > 0) {
            // The nodes are already persisted. If the edges fail the user is left with a string of
            // disconnected nodes that looks like a successful import, so undo the nodes rather than leave it.
            try {
              await addSpoolsMutation.mutateAsync({
                projectId: project.entityId,
                spools: spools.map(toSpoolImportDto),
              });
            } catch (spoolError) {
              try {
                await removeAddedNodes.mutateAsync({
                  projectId: project.entityId,
                  projectName: project.name,
                  nodeId: chainRows.map((row) => row.nodeId),
                  data: [],
                });
                enqueueSnackbar(i18n._('Could not connect the new nodes, so none were added.'), { variant: 'error' });
              } catch {
                enqueueSnackbar(
                  i18n._('Could not connect the new nodes and could not remove them again. They have been added without connections.'),
                  { variant: 'error', autoHideDuration: 8000 },
                );
              }

              return;
            }
          }

          closeDialog();
        } finally {
          setSubmitting(false);
        }
      },
      validationSchema: object().shape({
        nodes: array().of(BetweenNodesSchema),
      }),
    },
  });

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
  } = formik;

  // ---------- Idempotent preview infra ----------
  const lastPreviewKeyRef = useRef<string>('');
  const previewIdsRef = useRef<string[]>([]);
  const previewInFlightRef = useRef(false);

  const buildPreviewKey = (rows: Weaken<NodeRow>[]) => JSON.stringify(
    rows.map((r) => ({
      nodeId: r.nodeId || '',
      prev: (r.previousNodeId || []).slice().sort(),
      pipeType: r.pipeType || '',
      x: Number(r.x) || 0,
      y: Number(r.y) || 0,
      z: Number(r.z) || 0,
      endAtExistingNode: r.endAtExistingNode || '',
      pitName: r.pitName || '',
      frictionLoopLength: r.frictionLoopLength ?? null,
      area: r.area || '',
      level: r.level || '',
    })),
  );

  const removePreviousPreview = (m: Model) => {
    if (!previewIdsRef.current.length) return;

    const toRemoveNodes = previewIdsRef.current
      .map((id) => m.nodes[id])
      .filter(Boolean);

    toRemoveNodes.forEach((node) => {
      const spools = m.findSpoolsByNodeId(Object.values(m.spools), node.id);
      if (spools.length) m.removeSpools(spools);
    });

    if (toRemoveNodes.length) {
      m.removeNodes(toRemoveNodes as any);
    }

    previewIdsRef.current = [];
  };

  const handlePreview = React.useCallback(async () => {
    if (!model || previewInFlightRef.current) return;
    previewInFlightRef.current = true;

    try {
      const key = buildPreviewKey(values.nodes);
      if (key === lastPreviewKeyRef.current) {
        previewInFlightRef.current = false;
        return;
      }

      setValidationError(null);
      removePreviousPreview(model);

      const validationErrors = validateNodes(values.nodes);
      if (validationErrors.length > 0) {
        const msgs = validationErrors.flatMap((r) => r.errors);
        setValidationError(msgs.join('; '));
        lastPreviewKeyRef.current = key; // avoid looping on same invalid state
        previewInFlightRef.current = false;
        return;
      }

      const newPreviewIds = buildChainRows(values.nodes).map((row) => {
        const { newNode, spools } = createNodeAndSpool(row, model, undefined, PREVIEW_SPOOL_COLOUR);
        model.addNode(newNode);
        model.addSpools(spools);
        return newNode.id;
      });

      previewIdsRef.current = newPreviewIds;
      lastPreviewKeyRef.current = key;

      invalidate();
    } finally {
      previewInFlightRef.current = false;
    }
  }, [model, values.nodes]);

  // ---------- Auto-preview effect (debounced) ----------
  const [debouncedValues] = useDebounce(formik.values.nodes, 500);

  useEffect(() => {
    if (!model) return;

    const hasNodeId = values.nodes.some((n) => n.nodeId);

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0 && hasNodeId) {
        handlePreview();
      } else {
        const key = buildPreviewKey(values.nodes);
        lastPreviewKeyRef.current = key;
      }
    });
  }, [debouncedValues, model]);

  useEffect(() => (
    () => {
      if (model) removePreviousPreview(model);
      invalidate();
    }
  ), []);

  // ---------- Row helpers ----------
  const handleAddRow = () => {
    const newRow: Weaken<NodeRow> = {
      ...initialNodeRow,
      nodeId: `Node${formik.values.nodes.length + 1}`,
    };

    const updated = [...formik.values.nodes, newRow];

    if (updated.length > 1) {
      updated[updated.length - 2].endAtExistingNode = '';
    }

    if (updated.length > 1) {
      const prevId = updated[updated.length - 2].nodeId;
      updated[updated.length - 1].previousNodeId = [prevId!];
    }

    formik.setFieldValue('nodes', updated);
  };

  const handleNodeIdChange = (index: number, value: string) => {
    formik.setFieldValue(`nodes.${index}.nodeId`, value);
    for (let i = index + 1; i < formik.values.nodes.length; i += 1) {
      formik.setFieldValue(`nodes.${i}.previousNodeId`, [value]);
    }
  };

  const handleDeleteRow = (index: number) => {
    const newValues = [...formik.values.nodes];
    newValues.splice(index, 1);

    if (index < newValues.length && index > 0) {
      const prevId = newValues[index - 1].nodeId || '';
      newValues[index].previousNodeId = [prevId];
    }

    formik.setFieldValue('nodes', newValues);
  };

  const handleCoordinateChange = (field: string, value: string) => {
    setFieldValue(field, value);
  };

  // ---------- Render ----------
  return (
    <form onSubmit={handleSubmit}>
      {validationError && (
        <div
          style={{
            color: 'red',
            backgroundColor: '#ffebee',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px',
            border: '1px solid #ef9a9a',
          }}
        >
          {validationError}
        </div>
      )}

      <Box sx={{ overflow: 'auto' }}>
        <StringNodesTable
          model={model}
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          stringRows={values.nodes}
          onChange={handleChange}
          onBlur={handleBlur}
          hasError={helpers.hasError}
          getErrorHelpText={helpers.getErrorHelpText}
          handleStringRowChange={(field: string, value: string | number) => {
            if (field.startsWith('nodes.') && field.endsWith('.nodeId')) {
              const index = parseInt(field.split('.')[1], 10);
              handleNodeIdChange(index, value.toString());
            } else {
              setFieldValue(field, value);
            }
          }}
          handleCoordinateChange={handleCoordinateChange}
          handleDeleteRow={handleDeleteRow}
          handleAddRow={handleAddRow}
        />
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="contained" onClick={handleAddRow} startIcon={<Add />}>
          <Trans id="Add Column">Add Column</Trans>
        </Button>

        <FormikActions
          formik={formik}
          mutation={addStringNodesMutation}
          submitText={isSubmitting ? i18n._('Submitting...') : i18n._('Submit Nodes')}
          right={['reset', 'submit']}
        />
      </Box>
    </form>
  );
};

export default StringNodesForm;
